import {useDeepCompareEffect, useMemoizedFn, useRequest} from "ahooks";
import {history} from 'umi';
import {IChartTableParams} from "../../MyAnalyze/types";
import { getChartList } from '@dbl/services/chart';
import {useCallback, useMemo, useRef, useState} from "react";
import {getIsTemplatePageFn, safeParseBoolean} from "./utils";
import { saveDashboard, updateDashboard } from '@dbl/services/dashboard';
import {ADD_DASHBOARD} from "../../Home/GettingStart";
import {useGetDataLabGuideDataList} from "../../Home/hoooks";
import {isEmpty, unionBy} from "lodash-es";
import {showErrorMessagetip} from "@home/components/ErrorMessageTip/ErrorMessageTip";
import {DASHBOARD_NAME_REGEX, DATALAB_NULL} from '@dbl/core/global';
import {
  AI_ANALYSIS_TYPE,
  ChangeTabLayoutsType,
  EMPTY_USER_FILTER,
  type ILayoutItemFilter,
  type TLayoutFilterType,
  type TLayoutTextType,
} from '@dbl/core/dashboard';
import {createEmptyUserControl} from "@/pages/datalab/components/DashboardLayout/_utils";
import {uuid} from "tea-component/es/_util/uuid";
import { IChartDataState } from "./_type";

export const useGetChartList = (
  gameCode: string,
  system = 'datalab',
  task_ids?: string[],
  onBefore?: (...args: any[]) => void,
  onFinally?: (...args: any[]) => void,
  keywords?: string,
  sort?: 'asc' | 'desc',
  onlyMyCharts?: boolean,
) => {
  const pageRef = useRef<number>(1);
  const PAGE_SIZE = 20;
  const { defaulet, guideSubTab } = useGetDataLabGuideDataList();
  const [chartDataState, setChartDataState] = useState<IChartDataState | undefined>();
  const [total, setTotal] = useState<number>(0);

  const requestChartTable = () => {
    const params: IChartTableParams = {
      game_code: gameCode,
      sub_labels: [],
      all_selected: true,
      keywords: keywords || '',
      only_my_charts: onlyMyCharts || false,
      order_by: 'create_time',
      desc: sort === 'desc',
      page: pageRef.current,
      page_size: PAGE_SIZE,
      system,
      task_ids,
      type: [0, 1],
      // sub_labels: [{parent_label_name: "业务可见", name: "mcy-test"}, {parent_label_name: "仅自己可见", name: "mcy-test"}]
    };
    return getChartList(params);
  };

  const {
    loading: chartLoading,
    error: chartError,
    run: getChartListData,
  }: any = useRequest(requestChartTable, {
    manual: true,
    // 请不要随意修改这个key，全局上下文唯一key,后来者需要注意，使用这个hooks，返回的Promise是贯穿上下文的，也就是说只要run一遍，在组件的各个地方都能拿到最新返回值，把这个hooks放置在顶层，遵循数据自上而下流动
    cacheKey: ADD_DASHBOARD,
    onError: (e) => {
      console.log('requestChartTable error:', e);
    },
    onBefore,
    onFinally,
    onSuccess: (res: any) => {
      const pageList = res?.data_list || [];
      const rowCount = res?.row_count || pageList.length;
      // ✅ 添加数据校验
      if (!Array.isArray(pageList)) {
        console.error('Invalid data_list format:', res);
        return;
      }
      setTotal(rowCount);
      setChartDataState((prev: any) => {
        const prevList =
          pageRef.current === 1 ? [] : prev?.data_list || [];
        const newList = [...prevList, ...pageList];
        return {
          ...(res || {}),
          data_list: newList,
          row_count: rowCount,
        };
      });
    },
  });

  // 首次 & gameCode/keywords/sort/onlyMyCharts 变化时，请求第一页数据
  useDeepCompareEffect(() => {
    if (!gameCode) return;
    pageRef.current = 1;
    setChartDataState(undefined);
    setTotal(0);
    getChartListData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameCode, task_ids, keywords, sort, onlyMyCharts]);

  const tableRecords = useMemo(() => {
    if (isEmpty(chartDataState?.data_list) && guideSubTab) {
      return defaulet;
    }
    return chartDataState || [];
  }, [chartDataState, defaulet, guideSubTab]);

  const hasMore = useMemo(() => {
    const currentLength = chartDataState?.data_list?.length || 0;
    if (!total) return false;
    return currentLength < total;
  }, [chartDataState, total]);

  const { firstLoading, loadingMore } = useMemo(() => {
    const firstLoading = chartLoading && pageRef.current === 1;
    const loadingMore = chartLoading && pageRef.current > 1;
    return {
      firstLoading,
      loadingMore,
    };
  }, [chartLoading]);

  const loadMore = () => {
    if (!gameCode) return;
    if (chartLoading) return;
    if (!hasMore) return;
    pageRef.current += 1;
    getChartListData();
  };

  return {
    chartData: tableRecords,
    chartLoading,
    chartError,
    guideSubTab,
    hasMore,
    loadMore,
    firstLoading,
    loadingMore,
  };
};

export const useSaveModal = (props:any) => {
  const errorMessageRef = useRef('');
  const [modalLoading, setModalLoading] = useState(false);
  const {tabLayouts, gameCode, isEdit, dashboardId, closeModal, formatMessage, title, isTemplateReport, reqCfg, showContact, tab, tabId } = props;
  const validDashboardName = useCallback((name: string) => {
    const tempName = name?.trim();
    if (tempName?.length < 1) {
      errorMessageRef.current = formatMessage({id: 'dbl_common_cannot_be_empty'});
    } else if (tempName?.length > 50) {
      errorMessageRef.current = formatMessage({id: 'dbl_common_valid_name_length'}, {count: '50'});
    } else if (!DASHBOARD_NAME_REGEX.test(tempName)) {
      errorMessageRef.current = formatMessage({id: 'dbl_common_dashboard_valid_name'});
    } else errorMessageRef.current = '';
    
    return errorMessageRef.current;
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formatMessage]);
  
  const onSubmit = (value: { dashboard_title: string; }) => {
    const name = value?.dashboard_title;
    if (validDashboardName(name)) {
      // 点击图表卡片的编辑, 跳转之前的校验提示
      showErrorMessagetip({
        duration: 3000,
        errorCode: -1,
        content: errorMessageRef.current,
        isClose: true,
        type: 'error'
      });
      return;
    }
    setModalLoading(true);
    const view = JSON.stringify({
      tabs: tabLayouts?.length > 0
        ? tabLayouts
        : [{
          layouts: [],
          filters: [],
          tabId: '-1',
          tabName: '',
          userFilter: { controls: [] },
          enableCollapseFilter: false,
        }],
    });
    const ids: string[] = [];
    tabLayouts?.forEach(tab => {
      tab?.layouts?.forEach(item => {
        item.task_id && ids.push(item.task_id);
      });
    });
    const tempName = name?.trim() ?? '';
    let params: any = {
      game_code: gameCode,
      dashboard_title: tempName || title,
      description: '',
      json_metadata: view,
      chart_ids: ids.join(','), // TODO 为什么要上传图标id
      show_contact: showContact,
    };
    // 如果是单个图表编辑
    if(safeParseBoolean(isTemplateReport) && typeof value === 'string'){
      params.template_title = title;
    }
    // 如果是整个报表保存
    if(typeof value !== 'string'){
      params = {...value,...params};
    }
    if (isEdit) params.id = dashboardId;
    const confirmDashboard = isEdit ? updateDashboard : saveDashboard;
    confirmDashboard(params, reqCfg).then((res: any) => {
      errorMessageRef.current = '';
      const dashboardIdVal = (isEdit ? dashboardId : res?.id) || '';
      const dashboardNameVal = getIsTemplatePageFn(res?.category) ? res?.template_title : res?.dashboard_title;
      
      // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
      closeModal && closeModal();
      setModalLoading(false);
      // TODO: 待给标识，不然容易出bug
      if(typeof value === 'string'){
        history.push(`${value}&&dashboardId=${dashboardIdVal}&&dashboardName=${dashboardNameVal}&&isTemplateReport=${isTemplateReport}${tab ? `&&tab=${tab}` : ''}${tabId ? `&&tabId=${tabId}` : ''}`);
        return;
      }
      history.push({
        pathname: `/datalab/${gameCode}/WorkBench/MyDashboard/ViewPage`,
        query: {
          dashboardId: String(dashboardIdVal),
          dashboardName: dashboardNameVal,
          tab,
          tabId,
        }
      });
    }).catch((err) => {
      console.log(err);
      // 点击图表卡片的编辑, 跳转之前的校验提示
      // 999为取消接口标识
      err.code !== 999 && showErrorMessagetip({
        duration: 3000,
        errorCode: -1,
        content: err?.msg,
        isClose: true,
        type: 'error'
      });
    }).finally(() => {
      setModalLoading(false);
    });
  };
  
  return {
    errorMessageRef,
    validDashboardName,
    onSubmit,
    modalLoading
  };
};

const getAllChartTaskIds = (tabLayouts: any[] = []): string[] => {
  const taskIdSet = new Set<string>();
  for (const tabItem of tabLayouts) {
    for (const layoutItem of tabItem?.layouts || []) {
      if (layoutItem?.type === AI_ANALYSIS_TYPE || layoutItem?.chartType === AI_ANALYSIS_TYPE) {
        continue;
      }
      const taskId = String(layoutItem?.task_id || '');
      if (taskId) {
        taskIdSet.add(taskId);
      }
    }
  }
  return Array.from(taskIdSet);
};

const syncAiAnalysisScopeTaskIds = (tabLayouts: any[] = []): void => {
  const allChartTaskIds = getAllChartTaskIds(tabLayouts);
  const allChartTaskIdSet = new Set(allChartTaskIds);
  for (const tabItem of tabLayouts) {
    for (const layoutItem of tabItem?.layouts || []) {
      if (!(layoutItem?.type === AI_ANALYSIS_TYPE || layoutItem?.chartType === AI_ANALYSIS_TYPE)) {
        continue;
      }
      const analysisScope = layoutItem?.promptInfo?.analysisScope;
      if (!analysisScope) {
        continue;
      }
      if (analysisScope.type === 'all') {
        analysisScope.taskIds = allChartTaskIds;
        continue;
      }
      const chartTaskIds = Array.isArray(analysisScope.taskIds) ? analysisScope.taskIds : [];
      analysisScope.taskIds = chartTaskIds.filter((taskId: string) => allChartTaskIdSet.has(taskId));
    }
  }
};

// const USER_FILTER_DISPLAY_NAME = 'User Filter';
// const USER_FILTER_DEFAULT_GROUP_NAME = 'All Users';
//
// const buildUserFilterScopeCharts = (layouts: Array<TLayoutFilterType | any> = []): IUserFilterScopeChart[] => {
//   try {
//     return layouts
//       ?.filter((item: any) => item?.chartType && item?.chartType !== 'text' && item?.task_id)
//       .map((item: any) => ({
//         task_id: String(item.task_id),
//         name: item?.name,
//         nameCn: item?.name_cn,
//       })) || [];
//   } catch (e) {
//     console.error('buildUserFilterScopeCharts error', e);
//     return [];
//   }
// };

// const createDefaultUserFilterConfig = (layouts: Array<TLayoutFilterType | any> = []): IUserFilterConfig => ({
//   showScope: true,
//   scopeCharts: buildUserFilterScopeCharts(layouts),
//   userGroups: [{
//     id: uuid(),
//     name: USER_FILTER_DEFAULT_GROUP_NAME,
//     conditions: [],
//   }],
// });
export const useEditDashboardReportStore = (props) => {
  const {tabLayouts, setTabLayouts, currentTabId, setCurrentTabId, loadedChartsTabIds} = props;
  const [rightModalDragItem, setRightModalDragItem] = useState<any>(null); // 右侧鼠标拖动的卡片数据
  const [leftLayoutSelectItem, setLeftLayoutSelectItem] = useState<TLayoutFilterType | TLayoutTextType>(null); // 布局栏选中的组件
  // 添加页签的总数量
  const addTabCount = useRef(0);

  // 右侧编辑区ref
  const rightModalRef = useRef(null);

  // 更新路由tab信息
  const handleTabRouter = (newTab: { tabName: string; tabId: string; }) => {
    history.replace({
      pathname: history?.location?.pathname,
      query: {
        ...(history?.location?.query || {}),
        tab: typeof newTab?.tabName === 'string' ? newTab?.tabName : newTab?.tabId,
        tabId: newTab?.tabId,
      }
    });
  };
  
  // 更新 tabLayoutMap
  // 左侧Dashboard: 新增空筛选器, 删除筛选器, 改变筛选器位置 ()      右侧设置: 编辑保存筛选器()
  // 左侧Dashboard: 新增文本,  删除文本 ()     右侧设置:编辑保存文本
  // 左侧Dashboard: 新增图表, 删除图表
  // 头部header: 新增Tab, 删除Tab, 修改Tab, 改变位置
  const changeTabLayoutsReduce = useMemoizedFn((type, payload?) => {
    try {
      const currentTab = tabLayouts?.find(item => item.tabId === currentTabId) ?? {
        filters: [],
        userFilter: EMPTY_USER_FILTER,
        layouts: [],
        tabId: '-1',
        tabName: '',
        enableCollapseFilter: false,
      };
      !Array.isArray(currentTab.userFilter?.controls) && (currentTab.userFilter = EMPTY_USER_FILTER);
      
      switch (type) {
        // 页签tab
        case ChangeTabLayoutsType.ADD_TAB: {
          // 空报表情况下, 添加第一个页签
          // 已有筛选器或卡片的情况下, 添加第一个页签 (TODO 可能会再次触发图表请求, 需要避免!!!!)
          if (currentTabId === '-1') { // 说明当前是没有页签的情况
            currentTab.tabId = uuid(); // 直接修改原来的tab的
            currentTab.tabName = `Tab1`;
            currentTab.tabNameCN = `选项卡1`;
            addTabCount.current = 1;
            // 新增页签的图表不用再额外请求图表数据了
            loadedChartsTabIds.current.push(currentTab.tabId);
            
            setCurrentTabId(currentTab.tabId);
            handleTabRouter(currentTab);
            // TODO 三种情况:
            // 1: 筛选器和卡片都没有, 直接添加的页签, 那么 setTabLayouts([currentTab])
            // 2: 有筛选器和卡片, 添加第一个页签, 那么 setTabLayouts([currentTab]) 或者 setTabLayouts([...tabLayouts]);
            // 3. 已有页签的情况下, 添加新页签, 那么 setTabLayouts([...tabLayouts, newTabLayout]);
          } else {
            addTabCount.current = Math.max(tabLayouts.length + 1, addTabCount.current + 1);
            const newTab = {
              tabId: uuid(),
              tabName: `Tab${addTabCount.current}`,
              tabNameCN: `选项卡${addTabCount.current}`,
              filters: [],
              layouts: [],
              userFilter: { controls: [] },
              enableCollapseFilter: false,
            };
            // 新增页签的图表不用再额外请求图表数据了
            loadedChartsTabIds.current.push(newTab.tabId);
            
            setCurrentTabId(newTab.tabId);
            setTabLayouts([...tabLayouts, newTab]);
            handleTabRouter(newTab);
            return;
          }
          break;
        }
        case ChangeTabLayoutsType.DELETE_TAB: {
          // 删除最后一个tab
          const newTabLayouts = tabLayouts.filter(tab => tab.tabId !== payload.tabId);
          if (payload.tabId === currentTabId) {
            setCurrentTabId(newTabLayouts?.[0]?.tabId ?? '');
            // 拼接tab=null路由防止触发离开页面弹窗
            handleTabRouter(newTabLayouts?.[0] || { tabName: DATALAB_NULL, tabId: "" });
          }
          setTabLayouts(newTabLayouts);
          return;
        }
        case ChangeTabLayoutsType.EDIT_TAB_NAME: {
          const tab = tabLayouts.find(tab => (tab.tabId === payload.tabId));
          tab.tabName = payload.tabName;
          tab.tabNameCN = payload.tabNameCN;
          handleTabRouter(tab);
          break;
        }
        case ChangeTabLayoutsType.CHANGE_TAB_ORDER: {
          const newTabLayouts = payload.tabs.map(tab => tabLayouts.find(item => (item.tabId === tab.id)));
          setTabLayouts(newTabLayouts);
          return;
        }
        
        // 筛选器
        case ChangeTabLayoutsType.DELETE_FILTER: {
          if (payload.isRefresh) {
            // 需要刷新 图表
            const deleteFilter = currentTab.filters.find(data => data.i === payload.filterId);
            // TODO 如果 删除的filter中 有 relevantFilters 存在, 则子筛选器中要对应删除 dependFilters
            
            if (!deleteFilter) return;
            
            const newFilters = [];
            currentTab.filters.forEach(filter => {
              if (filter.i !== deleteFilter.i) {
                // 如果 删除的筛选器 被其他父筛选器 级联影响, 则需要在父筛选器中 删除掉
                if (deleteFilter.dependFilters?.some(item => item.i === filter.i)) {
                  filter.relevantFilters = filter?.relevantFilters?.filter((item) => item.i !== deleteFilter.i);
                  newFilters.push({...filter});
                } else if (deleteFilter.relevantFilters?.some(item => item.i === filter.i)) {
                  // 如果 删除的筛选器 是其他子筛选器的父级, 则需要在子筛选器中 删除掉
                  filter.dependFilters = filter?.dependFilters?.filter((item) => item.i !== deleteFilter.i);
                  newFilters.push({...filter});
                } else {
                  // 不会触发具体的筛选器组件更新
                  newFilters.push(filter);
                }
              }
              
            });
  
            currentTab.filters = newFilters;
            
            
          } else {
            // 删除空筛选器，不需要刷新图表
            const delIndex = currentTab.filters.findIndex(data => data.i === payload.filterId);
            currentTab.filters.splice(delIndex, 1);
            
          }
          
          setLeftLayoutSelectItem(null);
          break;
        }
        case ChangeTabLayoutsType.CHANGE_FILTER_ORDER: {
          if (!Array.isArray(payload.filters)) return;
          // 筛选器拖拽排序 不引起 图表重新计算
          currentTab.filters.splice(0, currentTab.filters.length);
          currentTab.filters.push(...payload.filters);
          
          break;
        }
        case ChangeTabLayoutsType.ADD_FILTER: {
          const newFilter: ILayoutItemFilter = {
            chartType: "filter",
            // chosen: false,
            // icon: "funnel",
            i: `filter-${Math.random()}`,
            // selected: false,
            title: "Filter",
            type: "filter"
          };
          currentTab.filters.push(newFilter); // 添加筛选器时, 不触发图表计算, 以及其他筛选器的级联, 所以这里使用push方法新增, 而不是改变引用地址
          // 增加 filter时, 右侧要同步展示筛选器配置
          setLeftLayoutSelectItem(newFilter);
          break;
        }
        case ChangeTabLayoutsType.UPDATE_FILTER: {
          // 更改filters的引用地址, 更新筛选器渲染
          const parentRelevantFilters = payload?.filterInfo?.relevantFilters;
          currentTab.filters = currentTab.filters.map((child) => {
            if (child.i === payload.filterInfo.i) {
              // 当筛选器变更了数据集或者筛选器类型, 则删除它依赖的父筛选器
              if (child.dataset !== payload.filterInfo.dataset || (payload.filterInfo.filterType !== 'string')) {
                child.dependFilters = [];
              }
              return {...child, ...payload.filterInfo};
            }
  
            // 更新的 payload?.filterInfo 是 其他筛选器的子筛选器的情况
            if (child.relevantFilters?.some?.((item) => item.i === payload.filterInfo.i)) {
              
              // 如果变更了数据集或者筛选器类型, 则在它的父筛选器中删除关联的子筛选器
              if (payload.filterInfo.dataset !== child.dataset || (payload.filterInfo.filterType  !== 'string')) {
                child.relevantFilters = child.relevantFilters.filter(item => item.i !== payload.filterInfo.i);
                
              } else {
                child.relevantFilters = unionBy([{i: payload.filterInfo.i, filterName: payload.filterInfo.filterName}], child.relevantFilters ?? [], 'i');
              }
            }
            
            /**
             * 更新的payload.filterInfo是其他筛选器的父筛选器的情况:
             *    parentRelevantFilters(子筛选器)中存在子筛选器child
             *    parentRelevantFilters.some?.((item) => item.i === filter.i)
             */
            if (parentRelevantFilters?.some?.((item) => item.i === child.i)) {
              // 父筛选器中 新增或修改 级联的子筛选器
              child.dependFilters = unionBy([{i: payload.filterInfo.i, filterName: payload.filterInfo.filterName}], child.dependFilters ?? [], 'i');
              return {...child};
            } 
            const newDependFilters = child?.dependFilters?.filter(item => item.i !== payload.filterInfo.i);
            if (newDependFilters?.length < child?.dependFilters?.length) { // TODO
              return {...child, dependFilters: newDependFilters};
            }
            
            return {...child};
          });

          break;
        }
        case ChangeTabLayoutsType.CLEAR_FILTERS: {
          currentTab.filters = [];
          break;
        }
        
        // User Filter
        case ChangeTabLayoutsType.INIT_USER_FILTER: {
          const { showScope=true, scopeCharts, controls} = payload || {};
          const i = `filter-${Math.random()}`;
          const newFilter: ILayoutItemFilter = {
            chartType: "userFilter",
            i,
            filterKey: i,
            title: "User Filter",
            type: "userFilter",
            controls: controls ?? [
              createEmptyUserControl()
            ],
            show_scope: showScope,
            scope_charts: scopeCharts, // 所有图表
          };
          currentTab.userFilter = newFilter;
          // 增加 filter时, 右侧要同步展示筛选器配置
          setLeftLayoutSelectItem(newFilter);
          break;
        }
        case ChangeTabLayoutsType.UPDATE_USER_FILTER: {
          const { showScope=true, scopeCharts, controls=[]} = payload || {};
          currentTab.userFilter = {
            ...currentTab.userFilter,
            show_scope: showScope,
            scope_charts: scopeCharts,
            controls
          };
          setLeftLayoutSelectItem(null);
          break;
        }
        case ChangeTabLayoutsType.DELETE_USER_FILTER: {
          currentTab.userFilter = { controls: [] };
          break;
        }
        case ChangeTabLayoutsType.ADD_USER_FILTER_CONTROL: {
          if (currentTab.userFilter) {
            const { controls } = currentTab.userFilter;
            currentTab.userFilter.controls = [
              ...controls,
              payload.newControl ?? []
            ]; 
          }
          break;
        }
        case ChangeTabLayoutsType.DELETE_USER_FILTER_CONTROL: {
          const { deleteId } = payload || {};
          if (Array.isArray(currentTab.userFilter.controls)) {
            const { controls } = currentTab.userFilter;
            currentTab.userFilter.controls = controls.filter((control) => control.id !== deleteId);
          }
          break;
        }
        case ChangeTabLayoutsType.UPDATE_USER_FILTER_CONTROL: {
          const { newControl } = payload || {};
          if (currentTab.userFilter) {
            // const { controls=[] } = currentTab.userFilter || {};
            currentTab.userFilter?.controls?.forEach((control) => {
              if (control.id === newControl.id) {
                Object.assign(control, newControl);
              }
            });
          }
          break;
        }

        case ChangeTabLayoutsType.UPDATE_COLLAPSE_FILTER: {
          currentTab.enableCollapseFilter = Boolean(payload?.enableCollapseFilter);
          break;
        }
        
        // 卡片
        case ChangeTabLayoutsType.DELETE_CARD: {
          // 删除卡片
          currentTab.layouts = currentTab.layouts.filter(data => data.i !== payload.cardInfo.i);
          // 删除卡片时, 筛选器的Scope范围更新
          if (currentTab?.type !== 'text') {
            currentTab.filters?.forEach((filterInfo) => {
              Array.isArray(filterInfo?.scope?.scope_charts) && (filterInfo.scope.scope_charts = filterInfo.scope.scope_charts.filter(chart => chart?.task_id !== payload.cardInfo?.task_id));
            });
            if (Array.isArray(currentTab.userFilter?.scope_charts)) {
              currentTab.userFilter.scope_charts = currentTab.userFilter?.scope_charts.filter(chart => chart?.task_id !== payload.cardInfo?.task_id);
            }
          }
          setLeftLayoutSelectItem(null);
          syncAiAnalysisScopeTaskIds(tabLayouts);
          break;
        }
        case ChangeTabLayoutsType.UPDATE_TEXT_CARD: {
          
          currentTab.layouts.some((layout) => {
            const flag = layout.i === payload.cardInfo.i;
            if (flag) {
              Object.assign(layout, payload.cardInfo);
              delete layout.isDraggable;
              delete layout.isResizable;
            }
            return flag;
          });
          
          break;
        }
        case ChangeTabLayoutsType.UPDATE_AI_ANALYSIS_CARD: {
          const { cardId, promptInfo } = payload || {};
          if (!cardId || !promptInfo) {
            break;
          }
          currentTab.layouts = currentTab.layouts.map((layout) => {
            const shouldUpdate = layout.i === cardId
              && (layout.chartType === AI_ANALYSIS_TYPE || layout.type === AI_ANALYSIS_TYPE);
            if (!shouldUpdate) {
              return layout;
            }
            return {
              ...layout,
              promptInfo,
            };
          });
          break;
        }
        case ChangeTabLayoutsType.UPDATE_CARD_LAYOUT: {
          // 更新图表布局, 或者同时增加图表卡片
          currentTab.layouts = payload.newLayouts.map(info => {
            
            const oldInfo = currentTab.layouts.find(old => old.i === info.i) || payload.cardInfo || {};
            const result = {
              ...oldInfo,
              ...info,
              // 删除这两个属性, 避免在报表预览页面, 还可以拖动卡片
              isDraggable: undefined,
              isResizable: undefined,
            };
            // 出现过 报表保存的数据中 x 和 y是null的情况, 导致页面一直加载不出来, 所以这里做个兜底
            // 确保 x 和 y 不是 null 或 undefined,确保 w 和 h 有有效值
            if (result.x == null || result.y == null || result.w == null || result.h == null)
              return oldInfo;
            
            return result;
          });
          // 增加的是Text卡片时, 右侧要同步展示文本配置
          if (payload?.cardInfo?.type === 'text') {
            setLeftLayoutSelectItem(payload.cardInfo);
          }
          syncAiAnalysisScopeTaskIds(tabLayouts);
          break;
        }
      }
      
      if (tabLayouts.length === 0) { // 新建报表 初始添加
        setTabLayouts([currentTab]);
        setCurrentTabId('-1');
      } else { // 报表编辑更新
        setTabLayouts([...tabLayouts]);
      }
    } catch (e) {
      console.error("报表编辑 changeTabLayoutsReduce error: ", e);
    }
    
  });

  return {
    rightModalDragItem,
    setRightModalDragItem,
    leftLayoutSelectItem,
    setLeftLayoutSelectItem,
    changeTabLayoutsReduce,
    rightModalRef,
  };
};

